import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

export interface StudentRow {
  id: string;
  level: string | null;
  group_id: string | null;
  parent_name: string | null;
  parent_phone: string | null;
  register_date: string | null;
  performance: string | null;
  attendance: string | null;
  notes: string | null;
}

export interface StudentGroup {
  id: string;
  name: string;
  level: string | null;
  color: string | null;
  day: string | null;
  time: string | null;
  active: boolean;
  whatsapp_link: string | null;
}

export interface StudentLesson {
  id: string;
  title: string;
  group_id: string | null;
  level: string | null;
  subject: string | null;
  date: string | null;
  start_time: string | null;
  end_time: string | null;
  type: string | null;
  live_link: string | null;
  description: string | null;
  status: string;
  has_record: boolean;
  has_document: boolean;
  has_assignment: boolean;
}

export interface StudentRecord {
  id: string;
  title: string;
  lesson_id: string | null;
  group_id: string | null;
  description: string | null;
  video_url: string | null;
  alt_url: string | null;
  published: boolean;
  visible_to_students: boolean;
  date: string | null;
  views: number;
  duration: string | null;
  created_at: string;
}

export interface StudentDocument {
  id: string;
  title: string;
  description: string | null;
  type: string | null;
  lesson_id: string | null;
  group_id: string | null;
  level: string | null;
  published: boolean;
  downloadable: boolean;
  upload_date: string | null;
  downloads: number;
  size: string | null;
  file_url: string | null;
  created_at: string;
}

export interface StudentAssignment {
  id: string;
  title: string;
  description: string | null;
  lesson_id: string | null;
  group_id: string | null;
  deadline: string | null;
  sent_date: string | null;
  status: string;
  required: boolean;
  reminder: boolean;
  has_file: boolean;
  link: string | null;
  file_url: string | null;
  target_type: string | null;
  target_students: string[] | null;
}

export interface StudentSubmission {
  id: string;
  assignment_id: string;
  student_id: string;
  status: string;
  note: string | null;
  submitted_at: string | null;
  file_url: string | null;
  file_path: string | null;
  file_name: string | null;
  file_type: string | null;
  file_size: number | null;
  link: string | null;
}

export interface StudentAnnouncement {
  id: string;
  title: string;
  message: string | null;
  group_id: string | null;
  date: string | null;
  priority: string;
  visible: boolean;
  views: number;
  created_at: string;
}

export interface StudentData {
  student: StudentRow | null;
  group: StudentGroup | null;
  isActive: boolean;
  lessons: StudentLesson[];
  records: StudentRecord[];
  documents: StudentDocument[];
  assignments: StudentAssignment[];
  submissions: StudentSubmission[];
  announcements: StudentAnnouncement[];
  whatsappLink: string | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

type DataState = Omit<StudentData, 'refetch'>;

const EMPTY: DataState = {
  student: null,
  group: null,
  isActive: false,
  lessons: [],
  records: [],
  documents: [],
  assignments: [],
  submissions: [],
  announcements: [],
  whatsappLink: null,
  loading: true,
  error: null,
};

export function useStudentData(): StudentData {
  const { user, profile } = useAuth();
  const [data, setData] = useState<DataState>(EMPTY);

  const fetchAll = useCallback(async () => {
    if (!user) {
      setData({ ...EMPTY, loading: false });
      return;
    }
    setData((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { data: studentData, error: studentError } = await supabase
        .from('zehrahoca_students')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();
      if (studentError) throw studentError;

      const student = (studentData ?? null) as StudentRow | null;
      const groupId = student?.group_id ?? null;

      // Yalnızca AKTİF grup geçerlidir; pasif grup içerikleri öğrenciye görünmez.
      let group: StudentGroup | null = null;
      if (groupId) {
        const { data: groupData, error: groupError } = await supabase
          .from('zehrahoca_groups')
          .select('*')
          .eq('id', groupId)
          .maybeSingle();
        if (groupError) throw groupError;
        const g = (groupData ?? null) as StudentGroup | null;
        group = g && g.active ? g : null;
      }

      const activeGroupId = group ? group.id : null;
      const isActive = profile?.active !== false && !!group;

      if (!activeGroupId || !isActive) {
        setData({
          student,
          group,
          isActive,
          lessons: [],
          records: [],
          documents: [],
          assignments: [],
          submissions: [],
          announcements: [],
          whatsappLink: null,
          loading: false,
          error: null,
        });
        return;
      }

      const [
        { data: lessonsData, error: lessonsError },
        { data: recordsData, error: recordsError },
        { data: docsData, error: docsError },
        { data: assignmentsData, error: assignmentsError },
        { data: submissionsData, error: submissionsError },
        { data: announcementsData, error: announcementsError },
      ] = await Promise.all([
        supabase
          .from('zehrahoca_lessons')
          .select('*')
          .eq('group_id', activeGroupId)
          .order('date', { ascending: true }),
        supabase
          .from('zehrahoca_lesson_records')
          .select('*')
          .eq('group_id', activeGroupId)
          .eq('published', true)
          .eq('visible_to_students', true)
          .order('date', { ascending: false }),
        supabase
          .from('zehrahoca_documents')
          .select('*')
          .eq('group_id', activeGroupId)
          .eq('published', true)
          .order('upload_date', { ascending: false }),
        supabase
          .from('zehrahoca_assignments')
          .select('*')
          .eq('group_id', activeGroupId)
          .order('deadline', { ascending: true }),
        supabase
          .from('zehrahoca_assignment_submissions')
          .select('*')
          .eq('student_id', user.id)
          .order('submitted_at', { ascending: false }),
        supabase
          .from('zehrahoca_announcements')
          .select('*')
          .eq('visible', true)
          .or(`group_id.eq.${activeGroupId},group_id.is.null`)
          .order('date', { ascending: false }),
      ]);

      if (lessonsError) throw lessonsError;
      if (recordsError) throw recordsError;
      if (docsError) throw docsError;
      if (assignmentsError) throw assignmentsError;
      if (submissionsError) throw submissionsError;
      if (announcementsError) throw announcementsError;

      // Seçili öğrencilere özel ödevler: sadece hedef öğrenci görsün.
      const assignments = ((assignmentsData ?? []) as StudentAssignment[]).filter((a) => {
        if (a.target_type === 'ogrenci' && Array.isArray(a.target_students) && a.target_students.length > 0) {
          return a.target_students.includes(user.id);
        }
        return true;
      });

      setData({
        student,
        group,
        isActive,
        lessons: (lessonsData ?? []) as StudentLesson[],
        records: (recordsData ?? []) as StudentRecord[],
        documents: (docsData ?? []) as StudentDocument[],
        assignments,
        submissions: (submissionsData ?? []) as StudentSubmission[],
        announcements: (announcementsData ?? []) as StudentAnnouncement[],
        whatsappLink: group.whatsapp_link ?? null,
        loading: false,
        error: null,
      });
    } catch (err) {
      setData((prev) => ({ ...prev, loading: false, error: (err as Error).message }));
    }
  }, [user, profile]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return { ...data, refetch: fetchAll };
}